import { doc, getDoc, setDoc, runTransaction } from 'firebase/firestore';
import { db, sanitizeFirestoreData } from './firebase';

export interface InvoiceSettings {
  // Branding & Company Details
  companyLogo: string;
  companyName: string;
  companyAddress: string;
  supportEmail: string;
  supportPhone: string;
  supportTelegram: string;
  websiteUrl: string;
  taxIdNumber: string;

  // Layout & Styling
  colorTheme: string; // e.g., '#0284c7' (Zoho Cyan/Blue) or '#0f172a'
  layoutDesign: 'zoho' | 'dark_enterprise' | 'classic_light';
  currency: string;
  currencySymbol: string;

  // Tax / VAT Settings
  enableTax: boolean;
  taxRate: number; // e.g., 6.25 or 15
  taxName: string; // e.g. "MA Tax (6.25%)" or "VAT (15%)"

  // Status Watermarks
  watermarks: {
    pending: string;
    approved: string;
    rejected: string;
    cancelled: string;
    manual: string;
    crypto: string;
    custom: string;
  };

  // Invoice Numbering & Due Dates
  invoicePrefix: string;
  invoiceNumberFormat: 'PREFIX_YEAR_NUM' | 'PREFIX_TIMESTAMP' | 'PREFIX_RANDOM';
  nextSequenceNumber: number;
  dueDateRule: 'DUE_ON_RECEIPT' | 'NET_7' | 'NET_15' | 'NET_30' | 'CUSTOM_DAYS';
  customDueDateDays: number;

  // Instructions & Footer
  footerText: string;
  paymentInstructions: string;
  bankDetails: {
    bankName: string;
    accountName: string;
    accountNumber: string;
    branchRouting: string;
    swiftIban: string;
  };
  cryptoDetails: {
    walletAddress: string;
    network: string;
    qrCodeUrl: string;
  };
  termsAndConditions: string;

  // Email Automation & Templates
  autoGenerateOnOrder: boolean;
  autoEmailOnStatusChange: boolean;
  sendEmailOnPending: boolean;
  sendEmailOnApproved: boolean;
  emailSubjectTemplate: string;
  emailHeaderTitle: string;
  emailBodyIntro: string;
  emailFooterNote: string;
  invoiceEmailTemplate: string;
  orderApprovedTemplate: string;
  orderRejectedTemplate: string;
  orderPendingTemplate: string;
}

import { getLocalSiteSettings } from './siteSettingsService';

export const DEFAULT_INVOICE_SETTINGS: InvoiceSettings = {
  companyLogo: '/assets/logo.png',
  companyName: 'RH EDITOGRAPHY',
  companyAddress: '4141 Hacienda Drive, Suite 100, Pleasanton, CA 94588, USA',
  supportEmail: 'support@rheditography.net',
  supportPhone: '+1 (925) 924-9500',
  supportTelegram: '@rheditography_support',
  websiteUrl: 'https://rheditography.net',
  taxIdNumber: 'TAX-99-5003373',

  colorTheme: '#0284c7',
  layoutDesign: 'zoho',
  currency: 'USD',
  currencySymbol: '$',

  enableTax: true,
  taxRate: 6.25,
  taxName: 'MA Tax (6.25%)',

  watermarks: {
    pending: 'PENDING PAYMENT',
    approved: 'PAID & VERIFIED',
    rejected: 'REJECTED',
    cancelled: 'CANCELLED',
    manual: 'PROFORMA INVOICE',
    crypto: 'CRYPTO PAYMENT',
    custom: 'CUSTOM INVOICE',
  },

  invoicePrefix: 'RHI-2026-',
  invoiceNumberFormat: 'PREFIX_YEAR_NUM',
  nextSequenceNumber: 50102340,
  dueDateRule: 'DUE_ON_RECEIPT',
  customDueDateDays: 7,

  footerText: 'Get your own official digital media licenses & templates at rheditography.net!',
  paymentInstructions: 'This payment was processed via secure gateway / credit card ending with 2203.',
  bankDetails: {
    bankName: 'Global International Bank',
    accountName: 'RH Editography Ltd',
    accountNumber: '110-238-9481920',
    branchRouting: 'Branch: Main Financial Center • Routing: 225271890',
    swiftIban: 'SWIFT: GIBKUS33XXX',
  },
  cryptoDetails: {
    walletAddress: '0x71C7656EC7ab88b098defB751B7401B5f6d8976F',
    network: 'USDT (TRC20 / ERC20) / BTC / ETH',
    qrCodeUrl: 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=0x71C7656EC7ab88b098defB751B7401B5f6d8976F',
  },
  termsAndConditions: 'This is a computer-generated invoice, no signature is required.',

  autoGenerateOnOrder: true,
  autoEmailOnStatusChange: true,
  sendEmailOnPending: false,
  sendEmailOnApproved: true,
  emailSubjectTemplate: 'Invoice {{invoiceNumber}} - RH Editography',
  emailHeaderTitle: 'RH EDITOGRAPHY INVOICE',
  emailBodyIntro: 'Dear {{customerName}}, thank you for your order! Attached is your invoice PDF.',
  emailFooterNote: 'For support or questions, please email support@rheditography.net or visit rheditography.net.',
  invoiceEmailTemplate: 'Dear {{customerName}},\n\nAttached is your official invoice {{invoiceNumber}} for total amount {{total}}.\n\nThank you for choosing RH Editography.',
  orderApprovedTemplate: 'Dear {{customerName}},\n\nYour order {{orderId}} payment has been verified and approved! Your invoice {{invoiceNumber}} and digital files are now available.\n\nBest regards,\nRH Editography',
  orderRejectedTemplate: 'Dear {{customerName}},\n\nWe could not verify payment for your order {{orderId}}. Please contact support@rheditography.net or retry checkout.\n\nRegards,\nRH Editography',
  orderPendingTemplate: 'Dear {{customerName}},\n\nWe have received your order {{orderId}}. Payment verification is pending. We will notify you once confirmed.\n\nThank you!',
};

const SETTINGS_DOC_ID = 'invoice_settings';
const SETTINGS_COLLECTION = 'site_settings';

export async function getInvoiceSettingsFromFirestore(): Promise<InvoiceSettings> {
  const siteSettings = getLocalSiteSettings();
  const brandLogo = siteSettings.siteLogoEmailUrl || siteSettings.siteLogoHeaderUrl || '/assets/logo.png';

  let currentSettings: InvoiceSettings = { ...DEFAULT_INVOICE_SETTINGS, companyLogo: brandLogo };

  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      currentSettings = { ...currentSettings, ...snap.data() } as InvoiceSettings;
    } else {
      const local = localStorage.getItem('rh_invoice_settings');
      if (local) {
        currentSettings = { ...currentSettings, ...JSON.parse(local) };
      }
    }
  } catch (err) {
    console.warn('[InvoiceSettings] Firestore fetch error, fallback to default/local:', err);
    try {
      const local = localStorage.getItem('rh_invoice_settings');
      if (local) {
        currentSettings = { ...currentSettings, ...JSON.parse(local) };
      }
    } catch (e) {}
  }

  // Purge legacy unsplash placeholder if present and restore genuine brand logo
  if (!currentSettings.companyLogo || currentSettings.companyLogo.includes('unsplash.com')) {
    currentSettings.companyLogo = brandLogo;
  }

  return currentSettings;
}

export async function saveInvoiceSettingsToFirestore(settings: InvoiceSettings): Promise<void> {
  try {
    localStorage.setItem('rh_invoice_settings', JSON.stringify(settings));
  } catch (e) {}

  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const sanitized = sanitizeFirestoreData(settings);
    await setDoc(docRef, sanitized, { merge: true });
    console.log('[InvoiceSettings] Saved invoice settings to Firestore.');
  } catch (err) {
    console.error('[InvoiceSettings] Failed to save settings to Firestore:', err);
    throw err;
  }
}

export async function getNextInvoiceSequenceAtomic(): Promise<number> {
  const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
  
  try {
    return await runTransaction(db, async (transaction) => {
      const sfDoc = await transaction.get(docRef);
      if (!sfDoc.exists()) {
        transaction.set(docRef, { nextSequenceNumber: 50102341 }, { merge: true });
        return 50102340;
      }
      const newSeq = (sfDoc.data().nextSequenceNumber || 50102340);
      transaction.update(docRef, { nextSequenceNumber: newSeq + 1 });
      return newSeq;
    });
  } catch (e) {
    console.error("Transaction failed: ", e);
    return Math.floor(100000 + Math.random() * 900000);
  }
}
